'use client';

import { Button, Card, Flex, Icon, IconButton, Popover, Portal, Stack, Text } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  LuBellRing,
  LuBox,
  LuChevronRight,
  LuCircleCheck,
  LuPackageX,
  LuRadio,
  LuWorkflow,
  LuX,
} from 'react-icons/lu';

import EditDeleteActions from '@components/common/EditDeleteActions';
import ExpandableSearchButton from '@components/common/ExpandableSearchButton';
import NeedDetailDialog from '@features/positions/needList/NeedDetailDialog';
import { formatDate } from 'helpers/dates';
import { useHref } from 'hooks/useHref';
import PoweredByDatanaErp from '@features/supply/PoweredByDatanaErp';
import { FilterIcon, PlusIcon } from 'vectors';

const itemRowHoverStyle = { opacity: 0.7 };

export interface NeedEntry {
  id: string;
  itemName: string;
  quantity: number;
  unit: string;
  deliveryDate: string | null;
  source: 'signal' | 'integration';
  integrationName: string | null;
  createdAt: string;
  status: 'open' | 'partially_covered' | 'covered';
  automationCount: number;
}

const fakeNeeds: NeedEntry[] = [
  {
    id: 'nl-1',
    itemName: 'Organic Wheat Flour',
    quantity: 5000,
    unit: 'kg',
    deliveryDate: '2026-04-15',
    source: 'integration',
    integrationName: 'Datana ERP',
    createdAt: '2026-03-10T08:00:00Z',
    status: 'partially_covered',
    automationCount: 2,
  },
  {
    id: 'nl-2',
    itemName: 'Cold-Pressed Olive Oil',
    quantity: 1200,
    unit: 'liters',
    deliveryDate: '2026-04-10',
    source: 'integration',
    integrationName: 'Datana ERP',
    createdAt: '2026-03-10T08:00:00Z',
    status: 'covered',
    automationCount: 0,
  },
  {
    id: 'nl-3',
    itemName: 'Steel Beams (I-Type)',
    quantity: 150,
    unit: 'units',
    deliveryDate: '2026-04-18',
    source: 'signal',
    integrationName: null,
    createdAt: '2026-03-15T10:00:00Z',
    status: 'open',
    automationCount: 1,
  },
  {
    id: 'nl-4',
    itemName: 'Hydraulic Cylinders',
    quantity: 80,
    unit: 'units',
    deliveryDate: null,
    source: 'signal',
    integrationName: null,
    createdAt: '2026-03-16T14:00:00Z',
    status: 'open',
    automationCount: 0,
  },
  {
    id: 'nl-5',
    itemName: 'Portland Cement',
    quantity: 20000,
    unit: 'kg',
    deliveryDate: '2026-04-14',
    source: 'integration',
    integrationName: 'Datana ERP',
    createdAt: '2026-03-12T09:00:00Z',
    status: 'partially_covered',
    automationCount: 1,
  },
  {
    id: 'nl-6',
    itemName: 'HVAC Units',
    quantity: 25,
    unit: 'units',
    deliveryDate: '2026-05-10',
    source: 'signal',
    integrationName: null,
    createdAt: '2026-03-20T11:00:00Z',
    status: 'open',
    automationCount: 0,
  },
];

const WORKFLOW_TRIGGER_WITHDRAWN = { trigger: 'commitment_withdrawn' };
const WORKFLOW_TRIGGER_DEADLINE = { trigger: 'delivery_deadline_approaching' };
const WORKFLOW_PICKER_TRIGGERS = { picker: 'triggers' };
const WORKFLOW_VIEW_LIST = { view: 'list' };

const statusConfig: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'orange.600' },
  partially_covered: { label: 'Partial', color: 'blue.600' },
  covered: { label: 'Covered', color: 'green.600' },
};

interface NeedCardProps {
  need: NeedEntry;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onViewDetails: (need: NeedEntry) => void;
  onMarkCovered: (id: string) => void;
  onCancel: (id: string) => void;
  onNavigateWorkflow: (params?: Record<string, string>) => void;
}

const NeedCard = memo(
  ({ need, onEdit, onDelete, onViewDetails, onMarkCovered, onCancel, onNavigateWorkflow }: NeedCardProps) => {
    const config = statusConfig[need.status];
    const isSignal = need.source === 'signal';
    const canAct = need.status === 'open' || need.status === 'partially_covered';

    const onClickViewDetails = useCallback(() => {
      onViewDetails(need);
    }, [onViewDetails, need]);

    const onClickMarkCovered = useCallback(() => {
      onMarkCovered(need.id);
    }, [onMarkCovered, need.id]);

    const onClickCancel = useCallback(() => {
      onCancel(need.id);
    }, [onCancel, need.id]);

    const onClickSupplierWithdrew = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_TRIGGER_WITHDRAWN);
    }, [onNavigateWorkflow]);

    const onClickSetReminder = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_TRIGGER_DEADLINE);
    }, [onNavigateWorkflow]);

    const onClickCreate = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_PICKER_TRIGGERS);
    }, [onNavigateWorkflow]);

    const onClickViewAutomations = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_VIEW_LIST);
    }, [onNavigateWorkflow]);

    const hasAutomations = need.automationCount > 0;

    return (
      <Card.Root w="full" borderColor="border.light" borderRadius="xl" bg="surface.background">
        <Card.Body p={0} gap={3}>
          <Flex direction="row" alignItems="center" gap={2} px={5} pt={5}>
            <Flex
              bg="surface.hover"
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="md"
              px={2}
              py={0.5}
            >
              <Text fontSize="xs" fontWeight="semibold" color={config.color} letterSpacing="wide">
                {config.label.toUpperCase()}
              </Text>
            </Flex>
            <Flex direction="row" alignItems="center" gap={1.5}>
              {need.source === 'integration' ? (
                <PoweredByDatanaErp label="Source" />
              ) : (
                <>
                  <LuRadio size={14} />
                  <Text fontSize="sm" fontWeight="medium" fontStyle="italic">
                    Signal
                  </Text>
                </>
              )}
            </Flex>
            {isSignal && (
              <Flex ml="auto">
                <EditDeleteActions id={need.id} onEdit={onEdit} onDelete={canAct ? undefined : onDelete} />
              </Flex>
            )}
          </Flex>
          <Flex flexDir="column" gap={3} px={5}>
            <Flex
              direction="row"
              alignItems="center"
              gap={2}
              cursor="pointer"
              _hover={itemRowHoverStyle}
              onClick={onClickViewDetails}
            >
              <LuBox size={18} />
              <Text fontSize="lg" fontWeight="medium">
                {need.itemName} — {need.quantity.toLocaleString()} {need.unit}
              </Text>
              <Icon as={LuChevronRight} boxSize="18px" ml="auto" flexShrink={0} />
            </Flex>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              alignItems={{ base: 'flex-start', md: 'center' }}
              fontWeight="semibold"
            >
              {need.deliveryDate != null ? (
                <Flex direction="row" alignItems="center" gap={2}>
                  <Text fontSize="xs" color="text.secondary">
                    Delivery by
                  </Text>
                  <Text fontSize="sm">{formatDate(need.deliveryDate)}</Text>
                </Flex>
              ) : (
                <Flex direction="row" alignItems="center" gap={2}>
                  <Text fontSize="xs" color="text.secondary">
                    Delivery
                  </Text>
                  <Text fontSize="sm" fontStyle="italic">
                    Flexible
                  </Text>
                </Flex>
              )}
              <Flex direction="row" alignItems="center" gap={2}>
                <Text fontSize="xs" color="text.secondary">
                  Created
                </Text>
                <Text fontSize="sm">{formatDate(need.createdAt)}</Text>
              </Flex>
            </Stack>
          </Flex>
          <Flex
            borderTopWidth="1px"
            borderColor="border.light"
            px={5}
            py={2.5}
            gap={2}
            justifyContent="flex-end"
          >
            <Popover.Root positioning={{ placement: 'bottom-end' }}>
              <Popover.Trigger asChild>
                <Button size="xs" variant="subtle" borderRadius="md">
                  <LuWorkflow size={14} />
                  Automate{hasAutomations ? ` · ${need.automationCount}` : ''}
                </Button>
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content w="auto" minW="250px" p={1} borderRadius="lg">
                    <Flex direction="column" gap={0.5}>
                      <Button
                        variant="ghost"
                        size="sm"
                        borderRadius="md"
                        justifyContent="flex-start"
                        onClick={onClickSupplierWithdrew}
                      >
                        <LuPackageX size={16} />
                        <Text fontSize="sm">Supplier withdrew</Text>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        borderRadius="md"
                        justifyContent="flex-start"
                        onClick={onClickSetReminder}
                      >
                        <LuBellRing size={16} />
                        <Text fontSize="sm">Set reminder</Text>
                      </Button>
                      <Flex borderTopWidth="1px" borderColor="border.light" mt={0.5} pt={0.5} direction="column" gap={0.5}>
                        <Button
                          variant="ghost"
                          size="sm"
                          borderRadius="md"
                          justifyContent="flex-start"
                          w="full"
                          onClick={onClickCreate}
                        >
                          <LuChevronRight size={14} />
                          <Text fontSize="sm" color="text.secondary">
                            Create new
                          </Text>
                        </Button>
                        {hasAutomations && (
                          <Button
                            variant="ghost"
                            size="sm"
                            borderRadius="md"
                            justifyContent="flex-start"
                            w="full"
                            onClick={onClickViewAutomations}
                          >
                            <LuWorkflow size={14} />
                            <Text fontSize="sm" color="text.secondary">
                              View automations ({need.automationCount})
                            </Text>
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
            {canAct && (
              <Button
                size="xs"
                variant="subtle"
                colorPalette="green"
                borderRadius="md"
                onClick={onClickMarkCovered}
              >
                <LuCircleCheck size={14} />
                Mark covered
              </Button>
            )}
            {canAct && (
              <Button
                size="xs"
                variant="subtle"
                colorPalette="orange"
                borderRadius="md"
                onClick={onClickCancel}
              >
                <LuX size={14} />
                Cancel need
              </Button>
            )}
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  },
);
NeedCard.displayName = 'NeedCard';

const MyNeedListPage = () => {
  const router = useRouter();
  const { procureHref, workflowsHref } = useHref();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNeed, setSelectedNeed] = useState<NeedEntry | null>(null);

  const onClickNewNeed = useCallback(() => {
    router.push(procureHref());
  }, [procureHref, router]);

  const onEditNeed = useCallback((_id: string) => {}, []);

  const onDeleteNeed = useCallback((_id: string) => {}, []);

  const onViewDetails = useCallback((need: NeedEntry) => {
    setSelectedNeed(need);
  }, []);

  const onCloseDetail = useCallback(() => {
    setSelectedNeed(null);
  }, []);

  const onMarkCovered = useCallback((_id: string) => {}, []);

  const onCancelNeed = useCallback((_id: string) => {}, []);

  const onNavigateWorkflow = useCallback(
    (params?: Record<string, string>) => {
      const href = workflowsHref(params);
      router.push(href);
    },
    [workflowsHref, router],
  );

  const filteredNeeds = useMemo(() => {
    if (searchQuery === '') {
      return fakeNeeds;
    }
    const q = searchQuery.toLowerCase();
    return fakeNeeds.filter((n) => n.itemName.toLowerCase().includes(q));
  }, [searchQuery]);

  return (
    <Flex flexDirection="column" h="full" p={4}>
      <Flex borderBottomWidth="1px" borderColor="border.light" pb={3} alignItems="center">
        <Flex ml="auto" gap={2} alignItems="center">
          <ExpandableSearchButton
            query={searchQuery}
            setQuery={setSearchQuery}
            size="xs"
            iconColor="text.secondary"
          />
          <IconButton variant="ghost" size="xs" borderRadius="md">
            <FilterIcon color="text.secondary" />
          </IconButton>
          <Button size="2xs" borderRadius="md" onClick={onClickNewNeed}>
            New need <PlusIcon />
          </Button>
        </Flex>
      </Flex>
      <Stack gap={6} pt={4}>
        {filteredNeeds.map((need) => (
          <NeedCard
            key={need.id}
            need={need}
            onEdit={onEditNeed}
            onDelete={onDeleteNeed}
            onViewDetails={onViewDetails}
            onMarkCovered={onMarkCovered}
            onCancel={onCancelNeed}
            onNavigateWorkflow={onNavigateWorkflow}
          />
        ))}
      </Stack>
      <NeedDetailDialog need={selectedNeed} onClose={onCloseDetail} />
    </Flex>
  );
};

export default memo(MyNeedListPage);
